/* eslint-disable indent */
/**
 * Geosuggest Component
 *
 * This is the new implementation using @vis.gl/react-google-maps and the Places API (New).
 * It replaces the legacy react-geosuggest library.
 *
 * Migration Notes:
 * - Uses fetchAutocompleteSuggestions instead of legacy getPlacePredictions
 * - Implements session token management for proper billing
 * - Transforms new API responses to legacy format for backward compatibility
 * - Requires GoogleMapsProvider wrapper at application root
 *
 * @see Migration Spec: Technical Migration from react-geosuggest to @vis.gl/react-google-maps
 */

import isString from "lodash/isString"
import isFunction from "lodash/isFunction"
import isEmpty from "lodash/isEmpty"
import get from "lodash/get"
import defer from "lodash/defer"
import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  forwardRef,
} from "react"
import { useMapsLibrary, useApiIsLoaded } from "@vis.gl/react-google-maps"
import getContentUrl from "utils-lib/getContentUrl"
import isIE from "utils-lib/isIE"
import css from "classnames"
import { ReactSVG } from "react-svg"
import { withLocalization } from "hoc-lib/localization"
import { logRocketTrack } from "utils-lib/logRocket"
import ContentLoader from "react-content-loader"
import InputAdornment from "component-lib/Base/components/InputAdornment"
import TextField from "component-lib/Base/components/TextField"
import useFeatureFlags from "hooks-lib/LaunchDarkly/useFeatureFlags"
import { getWindow } from "utils-lib/getWindow"
import { isNextApp } from "utils-lib/isNextApp"
import getFormattedGoogleAddress from "utils-lib/getFormattedGoogleAddress"
// eslint-disable-next-line import/no-named-as-default
import AddressConfirmation from "./components/AddressConfirmation/AddressConfirmation"
import "./Geosuggest.scss"
import InfoMessage from "./InfoMessage"
import { useAutocompleteSuggestions } from "./hooks/useAutocompleteSuggestions"
import {
  transformSuggestionsToLegacy,
  transformPlaceToLegacyGmaps,
} from "./utils/transformSuggestions"

const ssrWindow = getWindow()

export const GeosuggestSkeleton = () => (
  <ContentLoader className="geosuggest-skeleton">
    <rect x="0" y="0" rx="0" ry="0" width="100%" height="100%" />
  </ContentLoader>
)

export const strings = {
  en_CA: {
    defaultPlaceholder: `800 Capitol Street, Houston, Texas 77002`,
    ERROR: `Sorry We are unable to find your address, please re-enter your address or call us at 855-864-1221`,
  },
  fr_CA: {
    defaultPlaceholder: `4210 King St E, Kitchener, ON N2P 2G5, Canada`,
    ERROR: `Désolé  Nous n'arrivons pas à trouver votre adresse, 864-1221.
    veuillez saisir à nouveau votre adresse ou nous appeler au 855 864-1221`,
  },
}

/**
 * GeosuggestInput - Headless input component for the autocomplete
 * Replaces ReactGeosuggest with a native implementation
 * Compatible with MUI TextField inputComponent
 */
const GeosuggestInput = forwardRef(
  (
    {
      id,
      placeholder,
      value,
      onChange,
      onFocus,
      onBlur,
      onClick,
      onKeyDown,
      readOnly,
      autoComplete,
      className,
      "aria-label": ariaLabel,
      ...rest
    },
    ref,
  ) => (
    <input
      ref={ref}
      id={`geosuggest__input--${id}`}
      data-testid="Geosuggest - input"
      className={css("geosuggest__input", className)}
      type="text"
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      onFocus={onFocus}
      onBlur={onBlur}
      onClick={onClick}
      onKeyDown={onKeyDown}
      readOnly={readOnly}
      autoComplete={autoComplete}
      aria-label={ariaLabel}
      {...rest}
    />
  ),
)

GeosuggestInput.displayName = "GeosuggestInput"

export const Geosuggest = ({
  className = ``,
  customPlaceholder = ``,
  style = {},
  defaultItem = null,
  enableAddressConfirmation = false,
  enableShake = false,
  forceUpdateText = ``,
  infoMessage = ``,
  onGeosuggestChange = () => {},
  onSuggestIsSelected = () => {},
  setCustomerAddressFromGeocode = () => {},
  clearAdressOnEmptyInput = () => {},
  onSuggestFocus = () => {},
  onSuggestNoResults = () => {},
  onUpdateSuggests = () => {},
  showStreetsOnly = false,
  errorStyle = "",
  customerSelections = {},
  showLocationIcon = false,
  strings,
  inputClassName = ``,
  scrollableContainer = null,
  autoActivateFirstSuggest = true,
  error = "",
  ariaLabel = ``,
  isLandedOnComPage = false,
  label = ``,
  labelPostFix = ``,
  setIsDefaultLoaded = () => {},
  onKeyPress = () => {},
  variant = "outlined",
  customInputRef = null,
  setSMSSuggestions = () => {},
  transitLabel = "",
  isRegistrationForm = false,
  isHolidayScheduleUrl = false,
}) => {
  // Core state
  const [isFocused, setIsFocused] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [inputValue, setInputValue] = useState(get(defaultItem, `label`, ``))
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(
    autoActivateFirstSuggest ? 0 : -1,
  )

  // For address confirmation
  const [valueToConfirm, setValueToConfirm] = useState(``)
  const [suggestionsForConfirmation, setSuggestionsForConfirmation] = useState(
    [],
  )

  // IE handling
  const [readonly, setReadonly] = useState(isIE())
  const [isInteractedWith, setIsInteractedWith] = useState(false)
  const [changeTransit, setChangeTransit] = useState(transitLabel)

  // Refs - always call useRef unconditionally to satisfy React hooks rules
  const internalInputRef = useRef(null)
  const inputRef = customInputRef || internalInputRef
  const justSelectedRef = useRef(false)
  const suggestionsRef = useRef(null)
  // Tracks whether the user has actively typed, preventing false error dispatches
  // when inputValue is pre-populated from defaultItem rather than user input.
  const hasUserTypedRef = useRef(false)

  // Use the new Places API via our custom hook
  const placesLib = useMapsLibrary("places")
  const apiIsLoaded = useApiIsLoaded()

  // Memoize request options to prevent unnecessary re-renders and API calls
  const requestOptions = useMemo(
    () => ({
      includedRegionCodes: ["US", "CA"],
    }),
    [],
  )

  const {
    LDFFReleasePreviousAddressGettingPassedInGetMeStartedApi,
    LDFFReleaseCheckAvailabilityButtonStaysDisabled,
    LDFFReleasePreventEligibilityApiFailureForCityAndZip,
    LDFFReleaseBulkInvalidAddressRetainIssue,
  } = useFeatureFlags([
    "LDFFReleasePreviousAddressGettingPassedInGetMeStartedApi",
    "LDFFReleaseCheckAvailabilityButtonStaysDisabled",
    "LDFFReleasePreventEligibilityApiFailureForCityAndZip",
    "LDFFReleaseBulkInvalidAddressRetainIssue",
  ])

  // Autocomplete suggestions hook with region restrictions
  // Note: includedRegionCodes replaces legacy componentRestrictions.country
  const {
    suggestions: rawSuggestions,
    isLoading,
    resetSession,
  } = useAutocompleteSuggestions(inputValue, requestOptions)

  // Transform suggestions to legacy format for backward compatibility
  const suggestions = transformSuggestionsToLegacy(rawSuggestions)

  // Filter suggestions if showStreetsOnly is enabled
  const filteredSuggestions = showStreetsOnly
    ? suggestions.filter((suggest) => {
        const description = get(suggest, `description`, ``)
        const firstWord = description.split(` `)[0]
        const patt = /\d/g
        return patt.test(firstWord)
      })
    : suggestions

  // Handle forceUpdateText prop
  useEffect(() => {
    if (forceUpdateText && isString(forceUpdateText)) {
      setInputValue(forceUpdateText)
    }
  }, [forceUpdateText])

  // Handle transition label
  useEffect(() => {
    if (inputValue) {
      checkTransitionLabel()
    }
  }, [inputValue])

  useEffect(() => {
    const shouldUpdate = LDFFReleaseBulkInvalidAddressRetainIssue
      ? defaultItem?.label
      : defaultItem
    if (shouldUpdate) {
      setInputValue(get(defaultItem, "label", ""))
      // Reset the typed flag — value came from a prop, not from user input.
      hasUserTypedRef.current = false
    }
  }, [defaultItem, LDFFReleaseBulkInvalidAddressRetainIssue])

  useEffect(() => {
    if (isRegistrationForm) {
      onSuggestIsSelected({})
    }
  }, [])

  // Notify parent of suggestion updates
  useEffect(() => {
    // Skip all "no results" side-effects while the component is in a
    // "just selected" state (i.e. the user picked from the dropdown and
    // resetSession cleared the list). The flag stays true until new
    // suggestions arrive, preventing multiple intermediate effect runs
    // (isLoading toggle, resetSession clear, etc.) from firing the error.
    if (justSelectedRef.current) {
      // Only clear the guard once fresh suggestions are present, meaning
      // the user has started typing again after the selection.
      if (filteredSuggestions.length > 0) {
        justSelectedRef.current = false
      }
      return
    }

    // While a fetch is in-flight and no suggestions are available yet, skip
    // notifying parents to prevent a false "no results" error flash while the
    // API call is still pending.
    if (!(isLoading && filteredSuggestions.length === 0)) {
      // Only notify parents of suggestion updates / errors when the user has
      // actually typed something. Skipping this for pre-filled (defaultItem)
      // values prevents a false SET_ERROR_ON_SUGGEST dispatch on initial mount.
      if (hasUserTypedRef.current) {
        onUpdateSuggests(filteredSuggestions, inputValue)

        // Track suggestions for SMS flow
        if (isFunction(setSMSSuggestions)) {
          setSMSSuggestions(filteredSuggestions)
        }

        // Handle no results (only when loading is done and with correct arg order)
        if (inputValue && !isLoading && filteredSuggestions.length === 0) {
          onSuggestNoResults(filteredSuggestions, inputValue)
        }
      }
    }

    // Reset active suggestion index
    if (autoActivateFirstSuggest && filteredSuggestions.length > 0) {
      setActiveSuggestionIndex(0)
    }

    if (showStreetsOnly) {
      setIsDefaultLoaded()
    }
  }, [filteredSuggestions.length, isLoading])

  const checkTransitionLabel = () => {
    if (transitLabel) {
      const inputElement = ssrWindow.document.querySelector(
        "#geosuggest__input--geosuggest-input-AddressAddress",
      )?.value
      const inputElementCaFr = ssrWindow.document.querySelector(
        "#geosuggest__input--geosuggest-input-AdresseAdresse",
      )?.value
      if (inputElement || inputElementCaFr) {
        setChangeTransit(false)
      } else {
        setChangeTransit(true)
      }
    }
  }

  const handleInputChangeModified = (event) => {
    const userInput = event.target.value
    const inputType = get(event, `nativeEvent.inputType`, ``)
    const isAutofillEvent =
      inputType === `insertReplacementText` ||
      (typeof event?.target?.matches === `function` &&
        event.target.matches(`:-webkit-autofill`))

    hasUserTypedRef.current = true
    setInputValue(userInput)
    if (isAutofillEvent && !isEmpty(userInput)) {
      setCustomerAddressFromGeocode(userInput)
    } else if (isEmpty(customerSelections?.customerAddress)) {
      if (!isEmpty(userInput) || (!isFocused && !isEmpty(userInput))) {
        setCustomerAddressFromGeocode(userInput)
      }
    } else if (isEmpty(userInput)) {
      clearAdressOnEmptyInput()
    }
    // Pass null as the first arg to preserve the legacy (refs, value) signature
    // that consumers such as MethodSelectorInput expect.
    onGeosuggestChange(null, userInput)
  }

  const handleInputChange = (event) => {
    const userInput = event.target.value
    hasUserTypedRef.current = true
    setInputValue(userInput)
    // Pass null as the first arg to preserve the legacy (refs, value) signature
    // that consumers such as MethodSelectorInput expect.
    onGeosuggestChange(null, userInput)
  }

  const handleFocus = () => {
    setIsFocused(true)
    onSuggestFocus()

    if (scrollableContainer) {
      scrollableContainer.addEventListener(`scroll`, handleOnScroll)
    }
  }

  const handleClick = () => {
    if (isIE()) {
      setReadonly(false)
    }
    if (!isInteractedWith) {
      logRocketTrack(`madlib-clicked-true`)
      setIsInteractedWith(true)
    }
  }

  const handleBlurModified = (event) => {
    const blurValue = get(event, `target.value`, ``)
    const latestInputValue = blurValue !== inputValue ? blurValue : inputValue

    // Browser autofill can update the input without triggering onChange.
    // Sync on blur so button state and redux address mirror manual entry behavior.
    if (blurValue !== inputValue) {
      hasUserTypedRef.current = true
      setInputValue(blurValue)
      if (!isEmpty(blurValue)) {
        setCustomerAddressFromGeocode(blurValue)
      } else if (isEmpty(blurValue)) {
        clearAdressOnEmptyInput()
      }
      onGeosuggestChange(null, blurValue)
    }

    if (isIE()) {
      setReadonly(true)
    }

    // Delay closing to allow click on suggestions
    setTimeout(() => {
      setIsFocused(false)
    }, 200)

    if (scrollableContainer) {
      scrollableContainer.removeEventListener(`scroll`, handleOnScroll)
    }

    // Address confirmation logic
    if (
      enableAddressConfirmation === true &&
      showConfirmation === false &&
      !isEmpty(filteredSuggestions) &&
      latestInputValue !== get(customerSelections, `customerAddress.label`, ``)
    ) {
      setShowConfirmation(true)
      setValueToConfirm(latestInputValue)
      setSuggestionsForConfirmation(filteredSuggestions)
    }

    if (latestInputValue || error) {
      logRocketTrack(`madlib-had-error-true`)
    }
  }

  const handleBlur = () => {
    if (isIE()) {
      setReadonly(true)
    }
    // Delay closing to allow click on suggestions
    setTimeout(() => {
      setIsFocused(false)
    }, 200)
    if (scrollableContainer) {
      scrollableContainer.removeEventListener(`scroll`, handleOnScroll)
    }
    // Address confirmation logic
    if (
      enableAddressConfirmation === true &&
      showConfirmation === false &&
      !isEmpty(filteredSuggestions) &&
      inputValue !== get(customerSelections, `customerAddress.label`, ``)
    ) {
      setShowConfirmation(true)
      setValueToConfirm(inputValue)
      setSuggestionsForConfirmation(filteredSuggestions)
    }

    if (inputValue || error) {
      logRocketTrack(`madlib-had-error-true`)
    }
  }

  const handleOnScroll = () => {
    const inputWrapper = ssrWindow.document.querySelector(
      `.${className} .geosuggest__input-wrapper`,
    )
    const suggestsWrapper = ssrWindow.document.querySelector(
      `.${className} .geosuggest__suggests-wrapper`,
    )

    if (inputWrapper && suggestsWrapper) {
      const { bottom } = inputWrapper.getBoundingClientRect()
      suggestsWrapper.style.top = `${bottom + ssrWindow.scrollY}px`
    }
  }

  /**
   * Handle suggestion selection - fetches place details and transforms to legacy format
   * Uses the new Places API methods: toPlace() and fetchFields()
   */
  const handleSuggestionSelect = useCallback(
    async (suggestion) => {
      if (!placesLib || !suggestion) return

      justSelectedRef.current = true

      try {
        // Get the original placePrediction from the new API
        const originalSuggestion = suggestion._original
        if (!originalSuggestion?.placePrediction) {
          console.warn("No placePrediction found in suggestion")
          return
        }

        // Convert prediction to Place object (new API method)
        const place = originalSuggestion.placePrediction.toPlace()

        // Fetch required fields - only request what's needed to minimize costs
        // Per the spec: 'location', 'displayName', 'formattedAddress'
        await place.fetchFields({
          fields: [
            "location",
            "displayName",
            "formattedAddress",
            "addressComponents",
          ],
        })

        // Transform to legacy format compatible with getFormattedGoogleAddress
        const legacyGmapsFormat = transformPlaceToLegacyGmaps(place, suggestion)

        // Format the address to extract all fields (lat, lng, country, postalCode, etc.)
        const formattedAddress = getFormattedGoogleAddress(
          legacyGmapsFormat,
          LDFFReleasePreventEligibilityApiFailureForCityAndZip,
        )

        // Update the input value
        setInputValue(suggestion.description || suggestion.label)

        // Reset the session token (billing requirement - closes the session)
        resetSession()

        // Call the callback with the fully formatted address
        onSuggestIsSelected(formattedAddress)

        // Close suggestions
        defer(() => {
          setIsFocused(false)
        })
      } catch (err) {
        console.error("Error selecting place:", err)
      }
    },
    [placesLib, resetSession, onSuggestIsSelected],
  )

  /**
   * Keyboard navigation for suggestions (Up/Down arrows, Enter, Escape)
   */
  const handleKeyDown = (event) => {
    const { key } = event

    if (key === "ArrowDown") {
      event.preventDefault()
      setActiveSuggestionIndex((prev) =>
        prev < filteredSuggestions.length - 1 ? prev + 1 : prev,
      )
    } else if (key === "ArrowUp") {
      event.preventDefault()
      setActiveSuggestionIndex((prev) => (prev > 0 ? prev - 1 : 0))
    } else if (key === "Enter") {
      event.preventDefault()
      if (
        activeSuggestionIndex >= 0 &&
        filteredSuggestions[activeSuggestionIndex]
      ) {
        handleSuggestionSelect(filteredSuggestions[activeSuggestionIndex])
      }
    } else if (key === "Escape") {
      setIsFocused(false)
    }

    // Call the original onKeyPress handler
    onKeyPress(event)
  }

  // Public methods (for ref forwarding compatibility with legacy component)
  const update = useCallback((text) => {
    setInputValue(text)
  }, [])

  const focus = useCallback(() => {
    inputRef.current?.focus()
  }, [])

  const selectSuggest = useCallback(
    (labelText) => {
      const match = filteredSuggestions.find(
        (s) => s.label === labelText || s.description === labelText,
      )
      if (match) {
        handleSuggestionSelect(match)
      }
    },
    [filteredSuggestions, handleSuggestionSelect],
  )

  // CSS classes
  const classNames = css(`Geosuggest`, {
    [className]: className,
    error: infoMessage,
    shake: enableShake,
    [`Geosuggest--focused`]: isFocused,
  })

  const formattedAddress = get(
    customerSelections,
    `customerAddress.formattedAddress`,
    ``,
  )
  const isReadonly = isEmpty(formattedAddress) ? false : readonly

  // Determine placeholder text based on holiday schedule URL
  const placeholderText = isHolidayScheduleUrl
    ? ""
    : transitLabel
    ? ""
    : customPlaceholder || strings.defaultPlaceholder

  const showSuggestions =
    isFocused && filteredSuggestions.length > 0 && !isLoading

  // Show skeleton if API is not loaded yet in Next.js environments
  if (isNextApp && !apiIsLoaded) {
    return <GeosuggestSkeleton />
  }

  return (
    <div
      data-testid="Geosuggest"
      className={classNames}
      style={{
        ...style,
      }}
    >
      {/* Screen reader announcements */}
      {(filteredSuggestions.length > 0 ||
        get(customerSelections, `showSuggestError`, false)) && (
        <div className="sr-only" aria-live="assertive">
          {get(customerSelections, `showSuggestError`, false)
            ? error || strings.ERROR
            : `${filteredSuggestions.length} suggestions are available, use up and down arrow to navigate them`}
        </div>
      )}

      <label
        className="sr-only"
        htmlFor={`geosuggest__input--geosuggest-input-${label.replace(
          ` `,
          `-`,
        )}${labelPostFix}`}
      >
        Address
      </label>

      <TextField
        variant={variant}
        placeholder={strings.defaultPlaceholder}
        label={label}
        fullWidth
        value={inputValue}
        onChange={
          LDFFReleaseCheckAvailabilityButtonStaysDisabled
            ? handleInputChangeModified
            : handleInputChange
        }
        onFocus={handleFocus}
        onBlur={
          LDFFReleaseCheckAvailabilityButtonStaysDisabled
            ? handleBlurModified
            : handleBlur
        }
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        InputLabelProps={{
          shrink: !isLandedOnComPage
            ? isFocused || !!inputValue
            : !changeTransit,
        }}
        InputProps={{
          startAdornment: showLocationIcon && (
            <InputAdornment position="start">
              <ReactSVG
                wrapper="span"
                src={getContentUrl(
                  "/content/dam/wm/icons/green-location-arrow-pin-icon.svg",
                )}
              />
            </InputAdornment>
          ),
          inputComponent: GeosuggestInput,
          inputProps: {
            ref: inputRef,
            id: `geosuggest-input-${label.replace(` `, `-`)}${labelPostFix}`,
            "aria-label": ariaLabel,
            placeholder: placeholderText,
            readOnly: isReadonly,
            autoComplete: "off",
            className: inputClassName,
          },
        }}
        data-testid="Geosuggest-input"
      />

      <div className="geosuggest__suggests-wrapper" ref={suggestionsRef}>
        <ul
          className={css("geosuggest__suggests", {
            "geosuggest__suggests--hidden": !showSuggestions,
          })}
          role="listbox"
        >
          {filteredSuggestions.map((suggestion, index) => (
            <li
              key={suggestion.placeId || index}
              className={css("geosuggest__item", {
                "geosuggest__item--active": index === activeSuggestionIndex,
              })}
              role="option"
              aria-selected={index === activeSuggestionIndex}
              onMouseDown={(e) => {
                e.preventDefault()
                handleSuggestionSelect(suggestion)
              }}
              onMouseEnter={() => setActiveSuggestionIndex(index)}
            >
              {suggestion.description}
            </li>
          ))}
        </ul>

        {/* Loading indicator */}
        {isLoading && inputValue && (
          <div className="geosuggest__loading" aria-live="polite">
            <span className="sr-only">Loading suggestions...</span>
          </div>
        )}
      </div>

      {infoMessage && (
        <div
          className={`maax-font-weight-regular error_message_wrapper ${
            infoMessage ? `InfoMessage--has-error` : ``
          }`}
          style={{ marginTop: `.5rem` }}
        >
          <InfoMessage>
            {infoMessage}
            {errorStyle}
          </InfoMessage>
        </div>
      )}
      {LDFFReleaseCheckAvailabilityButtonStaysDisabled && (
        <AddressConfirmation
          onClose={() => {
            setShowConfirmation(false)
          }}
          onSelect={(address) => {
            if (LDFFReleasePreviousAddressGettingPassedInGetMeStartedApi) {
              handleSuggestionSelect(address)
              update(get(address, `label`, ``))
            } else {
              update(address)
            }
            defer(() => {
              focus()
            })
          }}
          onConfirm={(labelText) => {
            selectSuggest(labelText)
          }}
          suggestions={suggestionsForConfirmation}
          open={showConfirmation}
          userInput={valueToConfirm}
          isFocused={isFocused}
          setCustomerAddressFromGeocode={setCustomerAddressFromGeocode}
          showLocationIcon={showLocationIcon}
        />
      )}
      {!LDFFReleaseCheckAvailabilityButtonStaysDisabled && (
        <AddressConfirmation
          onClose={() => {
            setShowConfirmation(false)
          }}
          onSelect={(address) => {
            if (LDFFReleasePreviousAddressGettingPassedInGetMeStartedApi) {
              handleSuggestionSelect(address)
              update(get(address, `label`, ``))
            } else {
              update(address)
            }
            defer(() => {
              focus()
            })
          }}
          onConfirm={(labelText) => {
            selectSuggest(labelText)
          }}
          suggestions={suggestionsForConfirmation}
          open={showConfirmation}
          userInput={valueToConfirm}
          showLocationIcon={showLocationIcon}
        />
      )}
    </div>
  )
}

export default withLocalization(strings)(Geosuggest)
